import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ChannelModelRecord, ChannelRecord, ModelRecord } from '@nadoutong/contracts';
import { useCreateChannelModel } from '../../api/useChannelModels.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Dialog, FormField, Input, Select } from '../../ui/index.ts';
import { coerceErrorMessage, useToast } from '../Toast.tsx';

/**
 * New SKU (epic #1327 PR-9): the marketplace owns SKU creation, because
 * the marketplace is where the pricing decision lives. The namespace
 * detail page — which used to carry a batch-listing modal — is a pure
 * catalog now.
 *
 * Two properties are load-bearing:
 *
 * 1. **The official-pricing gap blocks the submit** (spec decision a).
 *    A SKU's tenant price is a multiplier over the vendor's official
 *    list price, so listing a model that has none produces a SKU that
 *    cannot be billed — a failure the operator would only discover at
 *    the first real request. Rather than send them off to the vendor
 *    catalog and hope they come back, the gap is named inline with a
 *    deep link, and the submit stays disabled until the price exists.
 *
 * 2. **The sales guide multiplier is entered here, not inherited.** The
 *    old batch modal seeded every new SKU from the parent namespace
 *    (usually ×1.0) and left a warning telling the operator to go fix
 *    each one afterwards. Asking for the number at creation time is why
 *    this form replaces that flow rather than wrapping it.
 */

const DEFAULT_RATE = '1.0';

/** Models a namespace can still list: same vendor, listed, not already a SKU. */
function candidateModels(
  models: readonly ModelRecord[],
  channel: ChannelRecord | null,
  existing: readonly ChannelModelRecord[],
): readonly ModelRecord[] {
  if (channel === null) return [];
  const listed = new Set(
    existing.filter((sku) => sku.channel_id === channel.id).map((sku) => sku.model),
  );
  return models
    .filter(
      (candidate) =>
        candidate.vendor === channel.vendor &&
        candidate.deleted_at === null &&
        candidate.shelf_status === 'listed' &&
        !listed.has(candidate.model),
    )
    .sort((a, b) => a.model.localeCompare(b.model));
}

export function NewSkuDialog({
  open,
  onOpenChange,
  channels,
  models,
  modelsPending,
  modelsError,
  existingSkus,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly channels: readonly ChannelRecord[];
  readonly models: readonly ModelRecord[];
  /** True while the vendor catalog read is still in flight. */
  readonly modelsPending: boolean;
  /** Set when the vendor catalog read failed; `models` is then meaningless. */
  readonly modelsError: unknown;
  readonly existingSkus: readonly ChannelModelRecord[];
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const createMut = useCreateChannelModel();

  const [channelId, setChannelId] = useState('');
  const [model, setModel] = useState('');
  const [rate, setRate] = useState(DEFAULT_RATE);
  const [error, setError] = useState<string | null>(null);

  const channel = useMemo(
    () => channels.find((entry) => entry.id === channelId) ?? null,
    [channels, channelId],
  );
  const available = useMemo(
    () => candidateModels(models, channel, existingSkus),
    [models, channel, existingSkus],
  );
  const selectedModel = useMemo(
    () => available.find((entry) => entry.model === model) ?? null,
    [available, model],
  );

  // Absent and null both mean "no official price": a platform that
  // predates the field omits it, and the field is null when the vendor
  // catalog has no effective row. Neither can be priced against.
  const pricingMissing = selectedModel !== null && selectedModel.current_pricing == null;

  // A failed or in-flight catalog read yields the same empty candidate
  // array as a namespace that already sells everything. Collapsing the
  // three would report "nothing left to list" at the moment we could not
  // read the list at all — and, worse, would let the pricing gate pass on
  // a `current_pricing` we never fetched. They are kept distinct so the
  // dialog states which one is true and stays closed to submits in the
  // two cases where it does not know.
  const catalogUnavailable = modelsError !== null && modelsError !== undefined;
  const catalogUnknown = catalogUnavailable || modelsPending;

  const parsedRate = Number(rate.trim());
  const rateValid = rate.trim().length > 0 && Number.isFinite(parsedRate) && parsedRate > 0;

  const canSubmit =
    channel !== null
    && selectedModel !== null
    && !catalogUnknown
    && !pricingMissing
    && rateValid
    && !createMut.isPending;

  function close(): void {
    setChannelId('');
    setModel('');
    setRate(DEFAULT_RATE);
    setError(null);
    onOpenChange(false);
  }

  function submit(): void {
    if (!canSubmit || channel === null || selectedModel === null) return;
    setError(null);
    createMut.mutate(
      { channel_id: channel.id, model: selectedModel.model, rate_multiplier: parsedRate },
      {
        onSuccess: (response) => {
          const created = response.channel_model;
          toast.success(t('new_sku.toast.created', { sku: created.sku_name }));
          close();
          navigate(`/market/${encodeURIComponent(created.sku_id)}`);
        },
        onError: (err) => {
          setError(coerceErrorMessage(err));
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={t('new_sku.title')}
      description={t('new_sku.subtitle')}
      closeLabel={t('common.close')}
      testId="new-sku-dialog"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={createMut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!canSubmit}
            loading={createMut.isPending}
            data-testid="new-sku-submit"
          >
            {t('new_sku.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField
          fieldId="new-sku-namespace"
          label={t('new_sku.namespace_label')}
          hint={t('new_sku.namespace_hint')}
          required
        >
          <Select
            id="new-sku-namespace"
            testId="new-sku-namespace-select"
            mono
            value={channelId}
            placeholder={t('new_sku.namespace_placeholder')}
            ariaLabel={t('new_sku.namespace_label')}
            onValueChange={(next) => {
              setChannelId(next);
              // The model list is vendor-scoped to the namespace, so a
              // namespace change invalidates any pick made under the old one.
              setModel('');
              setError(null);
            }}
            options={channels.map((entry) => ({
              value: entry.id,
              label: `${entry.slug} · ${entry.vendor}`,
            }))}
          />
        </FormField>

        <FormField
          fieldId="new-sku-model"
          label={t('new_sku.model_label')}
          hint={channel === null ? t('new_sku.model_hint_pick_namespace') : t('new_sku.model_hint')}
          required
        >
          <Select
            id="new-sku-model"
            testId="new-sku-model-select"
            mono
            disabled={channel === null || catalogUnknown || available.length === 0}
            value={model}
            placeholder={
              catalogUnavailable
                ? t('new_sku.model_unavailable')
                : modelsPending
                  ? t('new_sku.model_loading')
                  : channel !== null && available.length === 0
                    ? t('new_sku.model_none')
                    : t('new_sku.model_placeholder')
            }
            ariaLabel={t('new_sku.model_label')}
            onValueChange={(next) => {
              setModel(next);
              setError(null);
            }}
            options={available.map((entry) => ({
              value: entry.model,
              label: entry.display_name === null ? entry.model : `${entry.model} · ${entry.display_name}`,
            }))}
          />
        </FormField>

        {catalogUnavailable ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-bad-line bg-bad-dim px-4 py-3 text-dense leading-5 text-bad-bright"
            data-testid="new-sku-models-unavailable"
          >
            <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('new_sku.models_unavailable', { error: coerceErrorMessage(modelsError) })}</span>
          </div>
        ) : null}

        {pricingMissing && selectedModel !== null ? (
          <div
            role="alert"
            className="flex flex-col gap-2 rounded-lg border border-warn-line bg-warn-dim/50 px-4 py-3 text-dense leading-5 text-warn"
            data-testid="new-sku-pricing-gap"
          >
            <span className="flex items-start gap-2">
              <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t('new_sku.pricing_gap', { model: selectedModel.model })}</span>
            </span>
            <Link
              to={`/supply/models/${encodeURIComponent(selectedModel.vendor)}/${encodeURIComponent(selectedModel.model)}`}
              data-testid="new-sku-pricing-link"
              className="inline-flex w-fit items-center gap-1.5 font-medium text-signal transition-colors duration-120 hover:text-paper"
            >
              <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
              {t('new_sku.pricing_gap_link')}
            </Link>
          </div>
        ) : null}

        <FormField
          fieldId="new-sku-rate"
          label={t('new_sku.rate_label')}
          hint={t('new_sku.rate_hint')}
          error={rate.trim().length > 0 && !rateValid ? t('new_sku.rate_invalid') : undefined}
          required
        >
          <Input
            id="new-sku-rate"
            mono
            inputMode="decimal"
            value={rate}
            invalid={rate.trim().length > 0 && !rateValid}
            onChange={(e) => setRate(e.target.value)}
            data-testid="new-sku-rate-input"
          />
        </FormField>

        {error !== null ? (
          <p className="m-0 text-dense text-bad-bright" role="alert" data-testid="new-sku-error">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
